import React, { useEffect, useState } from 'react';
import { Page, Text, View, Document, StyleSheet, Image, Font, PDFViewer } from '@react-pdf/renderer';
import { observer } from 'mobx-react';
import { formatNumber } from 'utils/formatHelper';
import { useParams } from 'react-router-dom';
import { wasteHandoverPlanPresenter } from 'stores/root_store';
import { WasteHandoverRegistrationDetailModel } from 'models/WasteHandoverRegistration';
import { Button, QRCode, Spin } from 'antd';
import moment from 'moment';
import logo from 'assets/images/logo-tiny.png';
import dayjs from 'dayjs';

// Register font
Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: `${window.location.origin}/Font/Roboto-Light.ttf`,
      fontWeight: 300,
      fontStyle: 'normal',
    },
    {
      src: `${window.location.origin}/Font/Roboto-LightItalic.ttf`,
      fontWeight: 300,
      fontStyle: 'italic',
    },
    {
      src: `${window.location.origin}/Font/Roboto-Regular.ttf`,
      fontWeight: 400,
      fontStyle: 'normal',
    },
    {
      src: `${window.location.origin}/Font/Roboto-Italic.ttf`,
      fontWeight: 400,
      fontStyle: 'italic',
    },
    {
      src: `${window.location.origin}/Font/Roboto-Medium.ttf`,
      fontWeight: 500,
      fontStyle: 'normal',
    },
    {
      src: `${window.location.origin}/Font/Roboto-MediumItalic.ttf`,
      fontWeight: 500,
      fontStyle: 'italic',
    },
    {
      src: `${window.location.origin}/Font/Roboto-Bold.ttf`,
      fontWeight: 700,
      fontStyle: 'normal',
    },
    {
      src: `${window.location.origin}/Font/Roboto-BoldItalic.ttf`,
      fontWeight: 700,
      fontStyle: 'italic',
    },
  ],
});

Font.registerHyphenationCallback(word => [word]);

// Create styles
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Roboto',
    position: 'relative',
    padding: "20px",
  },
  header: {
    flexDirection: 'row',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  footer: {
    flexDirection: 'row',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: "20px",
    paddingHorizontal: "50px"
  },
  footer2: {
    position: 'absolute',
    bottom: 10,
    left: 0,
    right: 8,
    textAlign: 'right',
    fontSize: 10,
  },
  title: {
    textTransform: 'uppercase',
    fontSize: 12,
    fontWeight: 700,
  },
  serial: {
    marginTop: "3px",
    fontStyle: 'italic',
    fontSize: 7,
    fontWeight: 400
  },
  imgBox: {
    width: "70px",
    height: "70px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "1px",
    borderColor: '#e7e7e7',
    borderRadius: '2px',
    marginTop: '14px',
  },
  img: {
    width: "65px",
    height: "65px",
  },
  infor: {
    marginTop: "10px",
    display: 'flex',
    flexDirection: 'row',
    justifyContent: "center",
    fontSize: "18px",
    gap: '10px'
  },
  inforText: {
    display: 'flex',
    fontSize: '10px',
    fontWeight: 400,
    paddingBottom: '2px',
    flexWrap: 'wrap'
  },
  textBold: {
    fontWeight: 500,
  },
  table: {
    marginTop: '10px',
    fontSize: '10px',
  },
  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    borderTop: 1,
    borderColor: 'black'
  },
  tableCell: {
    borderLeft: 1,
    borderColor: 'black',
    padding: '4px 2px',
    fontWeight: 400
  },
  contentTop: {
    display: 'flex',
    flexDirection: 'row',
    border: '1px solid #333',
    width: '100%',
  },
  logo: {
    width: 70,
    margin: "0 auto"
  },
  yellowBox: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    paddingHorizontal: '12px',
    paddingVertical: '8px',
    borderRadius: '4px',
    minWidth: '60px',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
  },
  textInsideBox: {
    fontSize: '10px',
    textAlign: 'center',
    marginTop: "-10px",
    marginLeft: "-50px"
  },
});

// Create Document Component
export function createPrintPDF() {
  return observer(() => {
    const { wasteHandoverRegistrationId } = useParams();
    const [dataDetail, setDataDetail] = useState<WasteHandoverRegistrationDetailModel | undefined>(undefined)
    const [qrImg, setQRImg] = useState<string | undefined>(undefined)

    // chi tiết phiếu đăng ký theo id
    const getDetailWasteHandoverRegistration = async () => {
      const detail = await wasteHandoverPlanPresenter.getDetailWasteHandoverRegistration(wasteHandoverRegistrationId || '')
      if (detail) {
        // Map vendorAddress từ API response
        const mappedDetail: WasteHandoverRegistrationDetailModel = {
          ...detail,
          vendorAddress: detail.vendorAddress || null
        }
        setDataDetail(mappedDetail)
      }
    }

    const downloadQRCode = () => {
      const canvas = document.getElementById('myqrcode')?.querySelector<HTMLCanvasElement>('canvas');
      if (canvas) {
        const base64String = canvas.toDataURL();
        setQRImg(base64String)
      }
    };

    useEffect(() => {
      downloadQRCode()
    }, [dataDetail]);

    useEffect(() => {
      getDetailWasteHandoverRegistration()
    }, []);

    return !dataDetail ?
      (<div className='w-full h-full flex justify-center items-center'>
        <Spin />
      </div>) :
      (<>
        <div id="myqrcode" className='hidden'>
          <QRCode value={dataDetail.qrCode || ''} bgColor="#fff" style={{ marginBottom: 16 }} />
        </div>
        <PDFViewer style={{ width: '100vw', height: '100vh' }}>
          <Document >
            <Page wrap size="A4" style={styles.page} orientation="landscape">
              <View style={styles.header}>
                <View style={styles.contentTop}>
                  <View
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                    }}
                  >
                    {/* Logo */}
                    <View style={{ padding: '12px', width: 'auto', borderRightWidth: '1px' }}>
                      <Image style={{
                        width: "70px",
                        margin: "0 auto",
                        marginTop: "12px"
                      }} src={logo} />
                    </View>
                    {/* QRCode */}
                    <View style={{ padding: '12px', paddingBottom: "4px", width: 'auto' }}>
                      {qrImg && <Image style={{
                        width: 80,
                        margin: "0 auto",
                        marginBottom: "10px"
                      }} src={qrImg} />}
                      <Text style={styles.serial}>Số phiếu: {dataDetail.wasteHandoverRegistrationCode}</Text>
                    </View>
                  </View>
                  {/* Thông tin bên phải */}
                  <View
                    style={{
                      borderLeft: '1px solid #333',
                      width: '80%',
                      fontSize: '12px',
                    }}
                  >
                    <View
                      style={{
                        borderBottom: '1px solid #333',
                        fontWeight: 500,
                        padding: '12px',
                        textAlign: 'center',
                      }}
                    >
                      <Text style={{ fontWeight: 700, textTransform: "uppercase" }}>Công ty AJINOMOTO Việt Nam</Text>
                    </View>
                    <View
                      style={{
                        fontWeight: 500,
                        padding: '12px',
                        textAlign: 'center',
                      }}
                    >
                      <Text style={{ fontWeight: 700, textTransform: "uppercase" }}>PHIẾU THU GOM CHẤT THẢI/ PHẾ LIỆU </Text>
                      <Text style={{ fontWeight: 400, textTransform: "uppercase", fontSize: "11px" }}>DELIVERY NOTE </Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.infor}>
                <View style={{ width: '50%' }}>
                  <Text style={styles.inforText}>
                    <Text style={styles.textBold}>Nhà cung cấp/Vendor: </Text>
                    {dataDetail.vendorNumber + " | "}{dataDetail.vendorName}
                  </Text>
                  <Text style={styles.inforText}>
                    <Text style={styles.textBold}>Địa chỉ/Address: </Text>
                    {dataDetail.vendorAddress || ''}
                  </Text>
                  <Text style={styles.inforText}><Text style={styles.textBold}>Tài xế/Driver:</Text> {dataDetail.driverName}</Text>
                </View>
                <View style={{ width: '50%' }}>
                  <View style={{
                    display: 'flex',
                    fontSize: '10px',
                    fontWeight: 400,
                    paddingBottom: '2px',
                    flexWrap: 'wrap'
                  }}>
                    <Text style={{ marginBottom: '2px' }}>
                      <Text style={styles.textBold}>Thời gian lấy hàng/Delivery Time:</Text>
                      {dataDetail.deliveryTimeItems && dataDetail.deliveryTimeItems.length ? dataDetail.deliveryTimeItems.map((item, index) => {
                        return (
                          <Text key={index}>
                            <Text> {dataDetail.periodTimeName ? dataDetail.periodTimeName + ", " : ""}</Text>
                            <Text>{item.deliveryTime ? moment(item.deliveryTime).format("DD/MM/YYYY HH:mm") : ''}</Text>
                          </Text>
                        )
                      }) : ''}
                    </Text>
                  </View>
                  <Text style={styles.inforText}><Text style={styles.textBold}>Số điện thoại/Tel:</Text> {dataDetail.phone || ''}</Text>
                  <Text style={styles.inforText}><Text style={styles.textBold}>Số xe/Truck No:</Text> {dataDetail.licensePlate}</Text>
                </View>
              </View>

              <View style={styles.infor}>
                <View style={{ width: '100%', border: "1px solid black", padding: "7px" }}>
                  <Text style={styles.inforText}><Text style={styles.textBold}>Đơn vị bàn giao/Tranfer:</Text> Công ty Ajinomoto Việt Nam</Text>
                  <Text style={styles.inforText}><Text style={styles.textBold}>Địa chỉ/Address: </Text> Khu công nghiệp Biên Hòa 1 - Trấn Biên - Đồng Nai - Việt Nam</Text>
                </View>
              </View>

              <View style={styles.table}>
                <View style={styles.tableRow}>
                  <View style={{ ...styles.tableCell, fontWeight: 500, width: '5%', textAlign: 'center' }}><Text>STT</Text></View>
                  <View style={{ ...styles.tableCell, fontWeight: 500, width: '15%', textAlign: 'center' }}><Text>Mã chất thải/ phế liệu</Text></View>
                  <View style={{ ...styles.tableCell, fontWeight: 500, width: '30%', textAlign: 'center' }}><Text>Tên chất thải/ phế liệu</Text></View>
                  <View style={{ ...styles.tableCell, fontWeight: 500, width: '25%', textAlign: 'center' }}><Text>Kho/ khu vực</Text></View>
                  <View style={{ ...styles.tableCell, fontWeight: 500, width: '25%', textAlign: 'center', borderRightWidth: 1 }}><Text>Ghi chú</Text></View>
                </View>
                {dataDetail?.deliveryInfoItems && dataDetail.deliveryInfoItems.map((item, index) => (
                  <View key={index} style={styles.tableRow}>
                    <View style={{ ...styles.tableCell, paddingTop: 2, paddingBottom: 2, width: '5%', textAlign: 'center' }}><Text style={{ marginVertical: "auto" }}>{index + 1}</Text></View>
                    <View style={{ ...styles.tableCell, paddingTop: 2, paddingBottom: 2, width: '15%' }}><Text style={{ marginVertical: "auto" }}>{item.productCode}</Text></View>
                    <View style={{ ...styles.tableCell, paddingTop: 2, paddingBottom: 2, width: '30%' }}><Text style={{ marginVertical: "auto" }}>{item.productName}</Text></View>
                    <View style={{ ...styles.tableCell, paddingTop: 2, paddingBottom: 2, width: '25%' }}><Text style={{ marginVertical: "auto" }}>{item.storageLocationName || item.storageLocation}</Text></View>
                    <View style={{ ...styles.tableCell, paddingTop: 2, paddingBottom: 2, width: '25%', borderRightWidth: 1 }}><Text style={{ marginVertical: "auto" }}>{item.note || ''}</Text></View>
                  </View>
                ))}
                <View style={styles.tableRow}></View>
              </View>


              <Text style={styles.footer2} render={({ pageNumber, totalPages }) => (
                `${pageNumber}/${totalPages}`
              )} fixed />
            </Page>
          </Document >
        </PDFViewer >
      </>)
  })
}

